import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLine, easeOut } from "./Reveal";
import { RESTAURANT, IMAGES } from "@/data/menu";
import logoMark from "@/assets/logo-mark.png";
import logoFull from "@/assets/logo-full.png";

export function Hero({ onNavigate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 pb-16 overflow-hidden"
    >
      {/* floating mascot watermark */}
      <motion.img
        src={logoMark}
        alt=""
        aria-hidden
        style={{ y: markY }}
        className="pointer-events-none select-none absolute -right-16 top-24 w-[320px] md:w-[520px] opacity-[0.06]"
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Full-width brand banner */}
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.15, ease: easeOut }}
          className="w-full mb-10 md:mb-16"
        >
          <img
            src={logoFull}
            alt="Udupi Paakamane — Pure Veg Cafe"
            className="w-full max-w-5xl mx-auto h-auto"
          />
        </motion.div>

        <motion.div style={{ y: textY }} className="grid lg:grid-cols-12 gap-10 items-end">
          {/* Headline */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="h-px w-10 bg-terracotta" />
              <span className="font-body text-xs md:text-sm uppercase tracking-[0.35em] text-terracotta">
                {RESTAURANT.tagline} · Est. Bengaluru
              </span>
            </motion.div>

            <h1 className="mb-2">
              <span className="block font-display italic text-terracotta text-5xl sm:text-6xl lg:text-7xl leading-[1.1]">
                <MaskLine delay={0.4}>Cooked like home.</MaskLine>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: easeOut }}
              className="font-body text-ink/70 text-base md:text-lg max-w-md mt-8 leading-relaxed"
            >
              Steaming idlis, crisp dosés, filter coffee and pure-vegetarian
              South Indian meals — served honestly in the lanes of
              Hanumanthanagar.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05, ease: easeOut }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <button
                data-testid="hero-cta-menu"
                onClick={() => onNavigate("menu")}
                className="rounded-full bg-olive text-cream px-8 py-4 font-body font-semibold text-sm tracking-wide hover:bg-terracotta transition-colors duration-300"
              >
                Explore the Menu
              </button>
              <button
                data-testid="hero-cta-visit"
                onClick={() => onNavigate("visit")}
                className="rounded-full border border-ink/20 text-ink px-8 py-4 font-body font-semibold text-sm tracking-wide hover:border-olive hover:text-olive transition-colors duration-300"
              >
                Find Us
              </button>
            </motion.div>
          </div>

          {/* Spotlight image in an arch */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: easeOut }}
              className="relative"
            >
              <div className="overflow-hidden rounded-t-[999px] rounded-b-2xl border border-line shadow-[0_30px_60px_rgba(78,98,44,0.12)]">
                <motion.img
                  style={{ y: imgY }}
                  src={IMAGES.heroDosa}
                  alt="Crisp masala dosé served with fresh chutneys"
                  className="w-full h-[440px] md:h-[560px] object-cover scale-110"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
                className="absolute -left-4 bottom-8 bg-cream border border-line rounded-2xl px-5 py-4 shadow-[0_20px_40px_rgba(78,98,44,0.10)]"
              >
                <p className="font-display italic text-2xl text-olive leading-none">100% Veg</p>
                <p className="font-body text-xs uppercase tracking-widest text-ink/50 mt-1">
                  7 AM – 9:15 PM
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.button
        onClick={() => onNavigate("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        data-testid="hero-scroll-indicator"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-ink/50 hover:text-olive transition-colors"
      >
        <span className="font-body text-[11px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}
